import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * A Ratezy-branded mock of Google's "Post a review" screen. Reached from the
 * customer review flow when the business is in DEMO status. Nothing is posted;
 * the copied review sits in the clipboard so the demo participant can paste it
 * into the visible textarea and see the flow end-to-end.
 */
export default function DemoGoogleReviewPage() {
  const [params] = useSearchParams();
  const businessName = params.get("name") || "Dream Café";

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [posted, setPosted] = useState(false);

  useEffect(() => {
    document.title = `${businessName} — Ratezy Demo`;
    document.body.style.background = "#ffffff";
  }, [businessName]);

  const canPost = rating > 0;

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: "'Roboto', 'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif",
        color: "#202124",
      }}
      data-testid="demo-google-page"
    >
      {/* Demo strip */}
      <div
        className="w-full text-center py-3 px-4"
        style={{ background: "#f1f3f4", borderBottom: "1px solid #e8eaed" }}
        data-testid="demo-banner"
      >
        <span style={{ color: "#5f6368", fontSize: 14 }}>
          <span style={{ color: "#5f6368" }}>Ratezy demo</span>
          <span className="mx-1.5">·</span>
          <span style={{ color: "#202124", fontWeight: 600 }}>No review will be published</span>
        </span>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-4 pb-10">
        {/* Header row: close icon + business name + Post */}
        <div className="flex items-center justify-between gap-3">
          <button
            aria-label="Close"
            className="p-1"
            onClick={() => window.close()}
            data-testid="demo-close"
            style={{ color: "#5f6368" }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <h1
            className="flex-1 text-[22px] font-medium truncate"
            style={{ color: "#202124" }}
            data-testid="demo-business-name"
          >
            {businessName}
          </h1>
          <button
            onClick={() => canPost && setPosted(true)}
            disabled={!canPost || posted}
            data-testid="demo-post-btn"
            style={{
              background: "#1a73e8",
              color: "#fff",
              borderRadius: 999,
              padding: "8px 22px",
              fontSize: 15,
              fontWeight: 500,
              opacity: (!canPost || posted) ? 0.55 : 1,
              cursor: (!canPost || posted) ? "not-allowed" : "pointer",
              border: "none",
              transition: "opacity 0.2s ease",
            }}
          >
            Post
          </button>
        </div>

        {posted ? (
          <div className="mt-16 text-center" data-testid="demo-posted-state">
            <div
              style={{
                width: 64, height: 64, borderRadius: 999, margin: "0 auto 20px",
                background: "rgba(52,168,83,0.12)", border: "2px solid #34a853",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#34a853" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2
              className="font-medium"
              style={{ fontSize: 24, color: "#202124" }}
              data-testid="demo-posted-heading"
            >
              Review posted
            </h2>
            <p
              className="mt-2 text-sm"
              style={{ color: "#5f6368" }}
              data-testid="demo-posted-note"
            >
              This is demo page only, no review is posted on live google page
            </p>
          </div>
        ) : (
          <>
            {/* User row */}
            <div className="mt-6 flex items-center gap-3">
              <div
                style={{
                  width: 44, height: 44, borderRadius: 999,
                  background: "#9aa0a6", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, fontWeight: 500,
                }}
              >
                D
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 500, color: "#202124" }}>Demo user</div>
                <div style={{ fontSize: 13, color: "#5f6368" }} className="flex items-center gap-1.5 mt-0.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="1.8">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" />
                  </svg>
                  <span>Demo review · Not connected to Google</span>
                </div>
              </div>
            </div>

            {/* Stars — clickable */}
            <div
              className="mt-6 flex justify-center gap-2"
              onMouseLeave={() => setHover(0)}
              data-testid="demo-stars"
            >
              {[1, 2, 3, 4, 5].map((s) => {
                const filled = (hover || rating) >= s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHover(s)}
                    data-testid={`demo-star-${s}`}
                    aria-label={`${s} star${s > 1 ? "s" : ""}`}
                    className="p-1 transition-transform active:scale-95"
                    style={{ background: "transparent", border: "none", cursor: "pointer" }}
                  >
                    <svg width="42" height="42" viewBox="0 0 24 24"
                      fill={filled ? "#f9ab00" : "#e8eaed"}
                      stroke={filled ? "#f9ab00" : "#dadce0"} strokeWidth="1">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </button>
                );
              })}
            </div>

            {/* Textarea */}
            <div className="mt-5">
              <textarea
                data-testid="demo-review-textarea"
                placeholder={`Share details of your own experience at this place`}
                rows={9}
                style={{
                  width: "100%",
                  border: "1px solid #dadce0",
                  borderRadius: 8,
                  padding: "18px 16px",
                  fontSize: 16,
                  color: "#202124",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                  minHeight: 200,
                }}
              />
            </div>

            <p className="mt-3 text-[13px]" style={{ color: "#5f6368" }} data-testid="demo-paste-hint">
              Paste your copied review here.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
